class LinkPrerenderer {
  constructor({
    selector = "a[href]",
    delay = 100,
    mobilePrefetchLinks = [],
    mobilePrerenderLinks = [],
    hoverPrerenderLinks = [], // NEW: simple list of path prefixes for hover-based prerender
    mobileMobileOnly = true,
    networkAware = true,
    useIdle = true,
    prefetchFallbackAs = "document",
  } = {}) {
    this.selector = selector;
    this.delay = delay;
    this.priorityPageLinks = [];
    if (document.querySelector(".ProductItem__Wrapper")) {
      this.priorityPageLinks = [
        ...new Set(
          [...document.querySelectorAll(".ProductItem__Wrapper a")].map((p) =>
            p.getAttribute("href")
          )
        ),
      ].splice(0, 10);
    }
    this.mobilePrefetchLinks = mobilePrefetchLinks;
    this.mobilePrerenderLinks = [
      ...this.priorityPageLinks,
      ...mobilePrerenderLinks,
    ];
    this.hoverPrerenderLinks = [
      ...this.priorityPageLinks,
      ...hoverPrerenderLinks,
    ];
    this.mobileMobileOnly = mobileMobileOnly;
    this.networkAware = networkAware;
    this.useIdle = useIdle;
    this.prefetchFallbackAs = prefetchFallbackAs;

    this.timeoutMap = new WeakMap();
    this.prefetched = new Set();
    this.init();
  }

  init() {
    document.addEventListener("mouseover", (e) => this.handleHover(e));
    document.addEventListener("mouseout", (e) => this.clearHover(e));

    // Mobile (mobile-only by default)
    if (!this.mobileMobileOnly || this.isMobile()) {
      const run = () => {
        this.prefetchMobilePrefetchLinks();
        this.prerenderMobilePrefetchLinks();
      };
      if (this.useIdle && "requestIdleCallback" in window) {
        requestIdleCallback(run, { timeout: 1500 });
      } else {
        setTimeout(run, 350);
      }
    }
  }

  // --- Hover logic: choose prerender or prefetch based on list ---
  handleHover(e) {
    const link = e.target.closest(this.selector);
    if (!link) return;

    const url = this.normalizeSameOriginUrl(link.href);
    if (!url || this.shouldSkipPrefetch()) return;

    const doAction = () => {
      if (this.matchesHoverPrerender(url)) {
        this.prerender(url);
      } else {
        this.prefetch(url);
      }
      this.timeoutMap.delete(link);
    };

    const timeoutId = setTimeout(doAction, this.delay);
    this.timeoutMap.set(link, timeoutId);
  }

  clearHover(e) {
    const link = e.target.closest(this.selector);
    if (!link) return;
    const id = this.timeoutMap.get(link);
    if (id) {
      clearTimeout(id);
      this.timeoutMap.delete(link);
    }
  }

  // --- Mobile prefetch / prerender ---
  prefetchMobilePrefetchLinks() {
    if (this.shouldSkipPrefetch()) return;
    for (const href of this.mobilePrefetchLinks) {
      const url = this.normalizeSameOriginUrl(href);
      if (url) this.prefetch(url);
    }
  }

  prerenderMobilePrefetchLinks() {
    if (this.shouldSkipPrefetch()) return;
    for (const href of this.mobilePrerenderLinks) {
      const url = this.normalizeSameOriginUrl(href);
      if (url) this.prerender(url);
    }
  }

  // --- Core actions ---
  prefetch(url) {
    const key = `prefetch:${url}`;
    if (this.prefetched.has(key)) return;
    if (document.querySelector(`link[rel="prefetch"][href="${url}"]`)) return;

    const el = document.createElement("link");
    el.rel = "prefetch";
    el.href = url;
    if (this.prefetchFallbackAs) el.as = this.prefetchFallbackAs;
    document.head.appendChild(el);
    this.prefetched.add(key);
  }

  prerender(url) {
    const key = `prerender:${url}`;
    if (this.prefetched.has(key)) return;
    if (document.querySelector(`link[rel="prerender"][href="${url}"]`)) return;

    const el = document.createElement("link");
    el.rel = "prerender";
    el.href = url;
    document.head.appendChild(el);
    this.prefetched.add(key);
  }

  // --- Helpers ---
  matchesHoverPrerender(url) {
    // Treat each entry as a path prefix (simple strings only)
    for (const prefix of this.hoverPrerenderLinks) {
      if (typeof prefix === "string" && url == prefix) return true;
    }
    return false;
  }

  normalizeSameOriginUrl(href) {
    try {
      const u = new URL(href, location.href);
      if (u.origin !== location.origin) return null;
      if (
        u.pathname === location.pathname &&
        (u.search === location.search || !u.search)
      )
        return null;
      return u.pathname + u.search;
    } catch {
      return null;
    }
  }

  shouldSkipPrefetch() {
    if (!this.networkAware) return false;
    const conn = navigator.connection;
    const saveData = conn && conn.saveData;
    const slow =
      conn &&
      typeof conn.effectiveType === "string" &&
      /(^|-)2g$|slow-2g/.test(conn.effectiveType);
    return !!(saveData || slow);
  }

  isMobile() {
    if (window.matchMedia("(pointer: coarse)").matches) return true;
    return Math.max(screen.width, screen.height) < 900;
  }
}

/* Usage */
new LinkPrerenderer({
  delay: 150,
  mobilePrefetchLinks: [
    "/",
    "/collections/outlet",
    "/products/hidratante-corporal-super-body",
    "/products/solucao-niacinamida",
    "/collections/shop-all",
    "/account",
    "/products/balm-de-olhos",
    "/products/solucao-gaba",
    "/collections/best-sellers",
  ],
  mobilePrerenderLinks: [
    "/",
    "/collections/outlet",
    "/collections/shop-all",
    "/collections/best-sellers",
  ],
  hoverPrerenderLinks: [
    "/",
    "/collections/outlet",
    "/collections/shop-all",
    "/collections/best-sellers",
  ],
  mobileMobileOnly: true, // only auto-prefetch these on mobile
  networkAware: true,
  useIdle: true,
});
